import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import { DefaultCheckBox, Holiday } from "src/components";

import { getTimeStr,SectionHeader, TimePicker, WeekDayPicker  } from ".";

const defaultHolidayObj = {
  checked: false,
  from: "07:00",
  to: "16:00",
};

const defaultHolidaysArr = [
  {
    date: "2025-01-01T00:00:00.000Z",
    label: "New Year's Day",
    description: "Public Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-01-06T00:00:00.000Z",
    label: "Three King's Day",
    description: "Regional Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-02-20T00:00:00.000Z",
    label: "Fasching",
    description: "Not A Public Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-03-08T00:00:00.000Z",
    label: "International Women's",
    description: "Regional Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-04-07T00:00:00.000Z",
    label: "Good Friday",
    description: "Public Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-04-09T00:00:00.000Z",
    label: "Easter Sunday",
    description: "Regional Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-04-10T00:00:00.000Z",
    label: "Easter Monday",
    description: "Public Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-05-01T00:00:00.000Z",
    label: "Labour Day",
    description: "Public Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-05-14T00:00:00.000Z",
    label: "Mother's Day",
    description: "Not A Public Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-05-18T00:00:00.000Z",
    label: "Father's Day",
    description: "Not A Public Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-05-13T00:00:00.000Z",
    label: "Ascension Day",
    description: "Public Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-05-29T00:00:00.000Z",
    label: "Whit Monday",
    description: "Public Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-06-08T00:00:00.000Z",
    label: "Corpus Christi",
    description: "Regional Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-08-15T00:00:00.000Z",
    label: "Assumption Day",
    description: "Regional Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-09-20T00:00:00.000Z",
    label: "World Children's Day",
    description: "Regional Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-10-03T00:00:00.000Z",
    label: "German Unity Day",
    description: "Public Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-10-31T00:00:00.000Z",
    label: "Reformation Day",
    description: "Regional Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-11-01T00:00:00.000Z",
    label: "All Saints' Day",
    description: "Regional Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-11-22T00:00:00.000Z",
    label: "Repentance Day",
    description: "Regional Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-12-25T00:00:00.000Z",
    label: "Christmas Day",
    description: "Public Holiday",
    ...defaultHolidayObj,
  },
  {
    date: "2025-12-26T00:00:00.000Z",
    label: "St. Stephen's Day",
    description: "Public Holiday",
    ...defaultHolidayObj,
  },
];

interface HolidayItemProps {
  label: string;
  isChecked: boolean;
  onChangeChecked: (newValue: boolean) => void;
  description: string;
  date: string;
  from: string;
  onFromChanged: (newValue: string) => void;
  to: string;
  onToChanged: (newValue: string) => void;
  isCanEditTime: boolean;
}

const HolidayItem = ({
  isChecked,
  onChangeChecked,
  label,
  description,
  date,
  from,
  onFromChanged,
  to,
  onToChanged,
  isCanEditTime,
}: HolidayItemProps) => {
  return (
    <Box
      className="holidayItem"
      sx={{
        margin: "0px 24px",

        borderBottom: "1px solid #F3F3F3",

        "&:last-child": {
          borderBottom: "none",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flex: 1,
          height: "48px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: "176px",
          }}
        >
          <DefaultCheckBox
            isChecked={isChecked}
            onChange={() => onChangeChecked(!isChecked)}
            sx={{
              marginRight: "8px",
            }}
          />
          <Typography
            sx={{
              color: "#363636",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            {label}
          </Typography>
        </Box>

        <Typography
          sx={{
            color: "#828282",
            fontSize: "14px",
            fontWeight: "400",
          }}
        >
          {description}
        </Typography>

        <Typography
          sx={{
            color: "#BF295C",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          {dayjs(date).format("MMM DD YYYY")}
        </Typography>
      </Box>

      {isCanEditTime && isChecked ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            paddingBottom: "8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "46px",
              minWidth: 150,
              paddingLeft: "34px",
              borderRight: "none",
            }}
          >
            <Typography
              sx={{
                fontWeight: "500",
                fontSize: "14px",
                color: "#BF295C",
                cursor: "default",
                textTransform: "uppercase",
              }}
            >
              Worktime
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flex: 1,
            }}
          >
            <TimePicker
              time={from}
              disabled={false}
              onChanged={onFromChanged}
            />
            <Typography
              sx={{
                color: "#4F4F4F",
                cursor: "default",
                fontSize: "14px",
                fontWeight: "400",
              }}
            >
              to
            </Typography>
            <TimePicker time={to} disabled={false} onChanged={onToChanged} />
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

interface HolidaySectionProps {
  holidaysArr: Array<Holiday>;
  typeOfHolidayId: string;
  onChange?: (holidays: any) => any;
}

export const HolidaySection = React.forwardRef(
  ({ holidaysArr, typeOfHolidayId, onChange }: HolidaySectionProps, ref) => {
    const year = "2023";
    const isCanEditTime = typeOfHolidayId !== "0";

    const normalizedHolidaysArray = useMemo(
      () =>
        holidaysArr?.filter((holiday) => {
          return isCanEditTime ? holiday.workHours : !holiday.workHours;
        }),
      [typeOfHolidayId, isCanEditTime, holidaysArr]
    );

    const getNormalizedHolidays = () => {
      return defaultHolidaysArr.map((holiday: any) => {
        const selectedHoliday = normalizedHolidaysArray?.find(
          (el: any) => el.name === holiday.label
        );

        if (selectedHoliday) {
          return {
            ...holiday,
            checked: true,
            ...(selectedHoliday.workHours
              ? {
                from: getTimeStr(
                  selectedHoliday.workHours?.from?.hours,
                  selectedHoliday.workHours.from?.minutes,
                  9,
                  0
                ),
                to: getTimeStr(
                  selectedHoliday.workHours?.to?.hours,
                  selectedHoliday.workHours.to?.minutes,
                  19,
                  0
                ),
              }
              : {}),
          };
        } else {
          return holiday;
        }
      });
    };

    const [holidays, setHolidays] = useState(getNormalizedHolidays());

    onChange && onChange(holidays)

    useEffect(() => {
      setHolidays(getNormalizedHolidays());
    }, [typeOfHolidayId]);

    const handleChangeChecked = (index: number) => (value: boolean) => {
      const weekDay = holidays[index];

      const updatedWeekDay = {
        ...weekDay,
        checked: value,
      };
      const newWeekDays = [
        ...holidays.slice(0, index),
        updatedWeekDay,
        ...holidays.slice(index + 1),
      ];

      setHolidays(newWeekDays);
    };

    const handleFromChanged = (index: number) => (value: string) => {
      const weekDay = holidays[index];

      const updatedWeekDay = {
        ...weekDay,
        from: value,
      };
      const newWeekDays = [
        ...holidays.slice(0, index),
        updatedWeekDay,
        ...holidays.slice(index + 1),
      ];
      setHolidays(newWeekDays);
    };

    const handleToChanged = (index: number) => (value: string) => {
      const weekDay = holidays[index];

      const updatedWeekDay = {
        ...weekDay,
        to: value,
      };
      const newWeekDays = [
        ...holidays.slice(0, index),
        updatedWeekDay,
        ...holidays.slice(index + 1),
      ];
      setHolidays(newWeekDays);
    };

    useImperativeHandle(ref, () => ({
      getHolidays: () => {
        const newHolidays = holidays.filter((el: any) => el.checked);

        return newHolidays?.map(({ from, to, date, label }) => {
          const timeFromStrArr = from.split(":");
          const timeToStrArr = to.split(":");

          return {
            date,
            name: label,
            ...(isCanEditTime
              ? {
                workHours: {
                  from: {
                    hours: +timeFromStrArr[0],
                    minutes: +timeFromStrArr[1],
                  },
                  to: {
                    hours: +timeToStrArr[0],
                    minutes: +timeToStrArr[1],
                  },
                },
              }
              : {}),
          };
        });
      },
    }));

    return (
      <Box>
        <SectionHeader label="Choose Holidays" />
        <Box
          sx={{
            maxHeight: "309px",
            boxShadow: "rgba(41, 66, 90, 0.12) 0px 0px 16px 0px",
            borderRadius: "6px",
            overflowY: "auto",
          }}
        >
          {holidays.map(
            ({ label, checked, from, to, date, description }, index) => {
              return (
                <HolidayItem
                  key={label}
                  label={label}
                  isChecked={checked}
                  date={date}
                  description={description}
                  isCanEditTime={isCanEditTime}
                  onChangeChecked={handleChangeChecked(index)}
                  from={from}
                  onFromChanged={handleFromChanged(index)}
                  to={to}
                  onToChanged={handleToChanged(index)}
                />
              );
            }
          )}
        </Box>
      </Box>
    );
  }
);
